<template>
  <teleport to="body">
    <div
      v-if="isPictureInPictureMode"
      ref="draggableContainer"
      class="pip-wrapper tw-flex tw-cursor-grab tw-absolute tw-rounded-md tw-overflow-hidden tw-bg-black"
    >
      <div
        class="wrapper-track-container tw-relative tw-flex tw-flex-1 tw-min-w-0"
        @mousedown="dragMouseDown"
      >
        <div
          v-if="isVoiceChatMode"
          class="tw-flex tw-justify-center tw-items-center tw-h-28 tw-w-44"
        >
          <div
            class="tw-bg-gray-8 tw-flex tw-justify-center tw-items-center tw-rounded-full tw-w-16 tw-h-16"
          >
            <div v-if="participantLabel" class="tw-font-bold tw-text-4xl">
              {{ participantLabel }}
            </div>
          </div>
        </div>
        <!-- High z-index needs to make this clickable -->
        <div
          class="tw-absolute tw-p-2 tw-z-10 tw-left-0 tw-right-0 tw-text-right tw-w-full tw-h-full"
        >
          <AppButton text @click="$emit('start')">
            <img
              width="24"
              height="24"
              src="@shared/assets/images/pip.svg?inline"
              alt="close picture in picture mode link icon"
            />
          </AppButton>
        </div>

        <div
          ref="pipContainer"
          class="tw-flex tw-relative tw-my-auto track-container video-size tw-max-h-full tw-max-w-full"
        >
          <div
            v-if="showParticipantAvatar"
            class="tw-text-white tw-flex tw-flex-col tw-m-auto tw-justify-center tw-items-center tw-w-full tw-h-full"
          >
            <ChatUserAvatar
              shape="circle"
              class="tw-mb-1"
              :size="50"
              :avatar="participantsLabels.first.avatar"
              data-avtar
            />
            <AppText
              color="white"
              weight="semibold"
              size="sm"
              as="div"
              data-name
            >
              {{ participantsLabels.first.name }}
            </AppText>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { computed } from 'vue'

import ChatUserAvatar from '@shared/components/chat/ChatUserAvatar.vue'
import EventBus, { VideoCallEvents } from '@shared/event-bus'
import { AttachTracksEvent } from '@shared/models/interfaces/conversations'
import { useVideoCallConfigsStore } from '@shared/store/video-call-configs'
import { useScreenSharing } from '@shared/utils/composables/video-call/use-screen-sharing'
import { useVideoCallParticipants } from '@shared/utils/composables/video-call/use-video-call-participants'
import { getInitials } from '@shared/utils/helpers/string-formatter'
import { handleUserFullName } from '@shared/utils/helpers/user-name'

import { Participant } from '@protos/chat/participant_pb'

import { PlainMessage } from '@bufbuild/protobuf'
import { useConversationsStore } from '@vc/store/conversations'

const props = defineProps<{
  participants: Array<PlainMessage<Participant>>
}>()

const videoCallConfigsStore = useVideoCallConfigsStore()
const conversationsStore = useConversationsStore()

const { isUserShareScreen, isActiveScreenSharing, isScreenSharingMode } =
  useScreenSharing()
const participants = computed(() => props.participants)

const { participantsLabels } = useVideoCallParticipants(participants)

const {
  isPictureInPictureMode,
  pictureInPictureIdentity,
  isParticipantHasActiveVideo,
  isVoiceChatMode,
  isVideoChatMode,
} = storeToRefs(videoCallConfigsStore)

defineEmits<{
  (event: 'start'): void
}>()

const clientX = ref(0)
const clientY = ref(0)
const movementX = ref(0)
const movementY = ref(0)

const draggableContainer = ref<HTMLDivElement>()
const pipContainer = ref<HTMLDivElement>()

const showParticipantAvatar = computed(() => {
  if (isScreenSharingMode.value) {
    return !isActiveScreenSharing.value || isUserShareScreen.value
  } else {
    return !isParticipantHasActiveVideo.value
  }
})
const patient = computed(() => conversationsStore.conversation.data?.patient)

const participantLabel = computed(() =>
  getInitials(
    handleUserFullName('', patient.value?.firstName, patient.value?.lastName)
  )
)

watch(
  () => isPictureInPictureMode,
  (isPictureInPicture) => {
    if (!isPictureInPicture.value && pipContainer.value) {
      pipContainer.value.innerHTML = ''
    }
  }
)

const tracksAttachHandler = (event?: AttachTracksEvent) => {
  if (event && isPictureInPictureMode.value) {
    event.tracks.forEach((track) => {
      if (!track) {
        videoCallConfigsStore.setScreenShareInProgress(false)
        return
      }
      const videoElement = track.attach() as HTMLVideoElement
      videoElement.disablePictureInPicture = true

      nextTick(() => pipContainer.value?.appendChild(videoElement))
      videoCallConfigsStore.setParticipantHasActiveVideo(true)
      // screen sharing logic
      track.on('started', () => {
        videoCallConfigsStore.setScreenShareInProgress(true)
        videoCallConfigsStore.setParticipantHasActiveVideo(true)
      })

      track.on('stopped', () => {
        if (!videoCallConfigsStore.isScreenSharing) {
          videoCallConfigsStore.setScreenShareInProgress(false)
        }
      })

      // Handle camera turned off (muted or disabled)
      track.on('disabled', () => {
        videoCallConfigsStore.setParticipantHasActiveVideo(false)
      })

      if (!event.isLocal && track?.kind === 'video') {
        // stop current user sharing the screen
        videoCallConfigsStore.onStopScreenSharing()
      }
    })
  }
}

const dragMouseDown = (event: MouseEvent) => {
  event.preventDefault()

  clientX.value = event.clientX
  clientY.value = event.clientY

  document.onmousemove = elementDrag
  document.onmouseup = closeDragElement
}

const getPositiveValue = (number: number): number => {
  return number < 0 ? 0 : number
}

const elementDrag = (event: MouseEvent) => {
  event.preventDefault()

  movementX.value = clientX.value - event.clientX
  movementY.value = clientY.value - event.clientY

  clientX.value = event.clientX
  clientY.value = event.clientY

  if (draggableContainer.value) {
    const { innerHeight, innerWidth } = window as Window
    const { offsetTop, offsetLeft, clientHeight, clientWidth } =
      draggableContainer.value

    const top = offsetTop - movementY.value
    const left = offsetLeft - movementX.value

    const maxHeight = innerHeight - clientHeight
    const maxWidth = innerWidth - clientWidth

    draggableContainer.value.style.top =
      top > maxHeight ? `${maxHeight}px` : `${getPositiveValue(top)}px`

    draggableContainer.value.style.left =
      left > maxWidth ? `${maxWidth}px` : `${getPositiveValue(left)}px`

    // Prevent dragging off screen
    // https://gitlab.com/qliqdev/qliq-bugs/-/issues/11835
    draggableContainer.value.style.right = 'unset'
  }
}

const closeDragElement = () => {
  document.onmouseup = null
  document.onmousemove = null
}

onMounted(() => {
  EventBus.on(VideoCallEvents.AttachTracks, tracksAttachHandler)

  // In case this component was reloaded from router navigation and
  // we lose video tracks and need to reattach without restarting call.
  if (pictureInPictureIdentity.value) {
    videoCallConfigsStore.startPictureInPictureMode(
      pictureInPictureIdentity.value
    )
  }
})
onBeforeUnmount(() => {
  if (!isVideoChatMode.value) {
    EventBus.off(VideoCallEvents.AttachTracks, tracksAttachHandler)
  }
})
</script>

<style scoped lang="scss">
.pip-wrapper {
  @apply landscape:tw-max-h-[35vh] landscape:tw-max-w-[20vw] portrait:tw-max-w-[35vw] portrait:tw-max-h-[20vh] tw-top-[5vh] tw-right-[5vw];
  // PDF Webviewer has z-index of 10000 for some reason and this component needs to appear on top
  z-index: 10001;
}

.pip-wrapper video {
  @apply tw-absolute tw-top-0 tw-left-0 tw-flex-1 tw-object-cover tw-min-w-0;
}

.wrapper-track-container .connection-icons {
  @apply tw-ml-0;
}

.video-size {
  @apply tw-bg-black tw-min-w-[200px] tw-min-h-[100px];
}
</style>
